import { useEffect, useState } from 'react';
import { useLazyGetConfigurationTableQuery } from '../api/gridApi';
import { parseConfigurationTables } from '../utils';
import type { GridData } from '../types';

export type ConfigurationTables = Record<string, GridData['data']>;

export type LoadTableFn = (
  args: { nodeKey: string },
  preferCacheValue: boolean
) => { unwrap: () => Promise<{ data: GridData['data'] }> };

const BATCH_SIZE = 5;

export async function loadConfigurationTables(
  value: string | undefined,
  loadTable: LoadTableFn,
  signal: { active: boolean }
): Promise<ConfigurationTables | null> {
  const entries = parseConfigurationTables(value);
  if (entries.length === 0) return null;

  const next: ConfigurationTables = {};

  for (let i = 0; i < entries.length; i += BATCH_SIZE) {
    if (!signal.active) return null;

    const batch = entries.slice(i, i + BATCH_SIZE);
    const results = await Promise.all(
      batch.map(async ({ alias, nodeKey }) => {
        try {
          const table = await loadTable({ nodeKey }, true).unwrap();
          return signal.active ? ([alias, table.data] as const) : null;
        } catch {
          return null;
        }
      })
    );

    for (const entry of results) {
      if (entry) next[entry[0]] = entry[1];
    }
  }

  if (!signal.active) return null;
  return next;
}

export function useConfigurationTables(value?: string): ConfigurationTables {
  const [tables, setTables] = useState<ConfigurationTables>({});
  const [loadTable] = useLazyGetConfigurationTableQuery();

  useEffect(() => {
    const signal = { active: true };

    void loadConfigurationTables(value, loadTable, signal).then((next) => {
      if (!signal.active) return;
      setTables(next ?? {});
    });

    return () => {
      signal.active = false;
      setTables({});
    };
  }, [value, loadTable]);

  return tables;
}
